import { Suspense, lazy } from 'react';
import { z } from 'zod';

import { useMarkWorkItemReadOnMount } from '@/features/work-items/hooks/useMarkWorkItemRead';
import { getProjectBySlugQuery } from '@/lib/supabase/queries/projects';
import { getWorkspaceBySlugQuery } from '@/lib/supabase/queries/workspaces';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate, useParams, useSearch } from '@tanstack/react-router';

const WorkItemDetailPage = lazy(() =>
  import('@/features/work-items/pages/WorkItemDetailPage').then((m) => ({
    default: m.WorkItemDetailPage,
  })),
);

// Origen desde el cual se abrio el detalle. Determina a donde apunta "Volver".
// Hoy solo se usa "my-work"; extensible a futuro (inbox, mentions, etc).
const searchSchema = z
  .object({
    from: z.enum(['my-work']).optional(),
  })
  .partial();

export const Route = createFileRoute('/_app/$wsSlug/projects/$projectSlug/$itemId')({
  validateSearch: searchSchema,
  component: ItemPage,
});

function ItemPage() {
  const { wsSlug, projectSlug, itemId } = useParams({
    from: '/_app/$wsSlug/projects/$projectSlug/$itemId',
  });
  const { from } = useSearch({ from: '/_app/$wsSlug/projects/$projectSlug/$itemId' });
  const navigate = useNavigate();

  const wsQ = useQuery(getWorkspaceBySlugQuery(wsSlug));
  const projQ = useQuery({
    ...getProjectBySlugQuery(wsQ.data?.id ?? '', projectSlug),
    enabled: !!wsQ.data?.id,
  });

  // Clear the "unread comments" badge as soon as the user opens the task.
  useMarkWorkItemReadOnMount(itemId);

  const cameFromMyWork = from === 'my-work';
  const goToBoard = () =>
    navigate({
      to: '/$wsSlug/projects/$projectSlug/board',
      params: { wsSlug, projectSlug },
    });
  const goToMyWork = () =>
    navigate({
      to: '/$wsSlug/my-work',
      params: { wsSlug },
    });

  return (
    <div className="h-full">
      <Suspense fallback={<div className="p-8 text-center text-quiet">Cargando detalle...</div>}>
        <WorkItemDetailPage
          itemId={itemId}
          projectIdentifier={projQ.data?.identifier ?? ''}
          onClose={cameFromMyWork ? goToMyWork : goToBoard}
          backLabel={cameFromMyWork ? 'Volver a Mi trabajo' : undefined}
          onNavigateToBoard={cameFromMyWork ? goToBoard : undefined}
          onNavigateToParent={(parentId) =>
            navigate({
              to: '/$wsSlug/projects/$projectSlug/$itemId',
              params: { wsSlug, projectSlug, itemId: parentId },
              search: from ? { from } : {},
            })
          }
          onNavigateToRecurrentes={() =>
            navigate({
              to: '/$wsSlug/projects/$projectSlug/recurrentes',
              params: { wsSlug, projectSlug },
            })
          }
        />
      </Suspense>
    </div>
  );
}
